import PermissionButton from '../../permissions/PermissionButton';
// components/clients/ClientStatementPrint.js
import React from 'react';
import { Button } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';

const formatCurrency = (amount) => {
  const value = Math.abs(amount).toFixed(2);
  return ` ${amount < 0 ? '-' : ''}${value}`;
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('fr-FR');
};

export const printClientStatement = (statement) => {
  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
        <title>Relevé Client - ${statement.clientInfo.name}</title>
        <style>
            body {
                font-family: Arial, sans-serif;
                margin: 20px;
                color: #333;
            }
            .header {
                text-align: center;
                margin-bottom: 30px;
                padding-bottom: 10px;
                border-bottom: 2px solid #333;
            }
            .section {
                margin-bottom: 20px;
            }
            .section-title {
                background-color: #f0f0f0;
                padding: 5px 10px;
                margin-bottom: 10px;
                font-weight: bold;
            }
            table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
            }
            th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
            }
            th {
                background-color: #f5f5f5;
            }
            .amount {
                text-align: right;
            }
            .negative {
                color: #ff4d4f;
            }
            .positive {
                color: #52c41a;
            }
            .summary-box {
                border: 1px solid #ddd;
                padding: 10px;
                margin-bottom: 20px;
                background-color: #fafafa;
            }
            .summary-item {
                margin: 5px 0;
            }
            @media print {
                .no-print {
                    display: none;
                }
                body {
                    margin: 0;
                    padding: 15px;
                }
            }
        </style>
    </head>
    <body>
        <div class="header">
            <h1>Relevé de Compte Client</h1>
            <p>Date d'édition: ${formatDate(new Date())}</p>
        </div>

        <div class="section">
            <div class="summary-box">
                <h2>Information Client</h2>
                <p><strong>Client:</strong> ${statement.clientInfo.name}</p>
                <p><strong>Compte:</strong> ${statement.clientInfo.account}</p>
                <p><strong>Solde actuel:</strong> 
                    <span class="${statement.clientInfo.currentBalance < 0 ? 'negative' : 'positive'}">
                        ${formatCurrency(statement.clientInfo.currentBalance)}
                    </span>
                </p>
            </div>
        </div>

        <div class="section">
            <div class="summary-box">
                <h2>Résumé</h2>
                <div class="summary-item">
                    <strong>Total Règlements:</strong> ${formatCurrency(statement.summary.totalReglements)}
                </div>
                <div class="summary-item">
                    <strong>Surplus Disponible:</strong> ${formatCurrency(statement.summary.totalSurplus)}
                </div>
                <div class="summary-item">
                    <strong>Factures Non Payées:</strong> ${formatCurrency(statement.summary.totalUnpaidFactures)}
                </div>
                <div class="summary-item">
                    <strong>Avoirs Non Utilisés:</strong> ${formatCurrency(statement.summary.totalUnusedAvoirs)}
                </div>
            </div>
        </div>

        ${statement.reglements.length > 0 ? `
            <div class="section">
                <div class="section-title">Règlements avec Montant Restant</div>
                <table>
                    <thead>
                        <tr>
                            <th>Référence</th>
                            <th>Date</th>
                            <th>Mode de Paiement</th>
                            <th class="amount">Montant Original</th>
                            <th class="amount">Montant Restant</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${statement.reglements
                            .filter(reg => reg.remainingAmount !== 0)
                            .map(reg => `
                                <tr>
                                    <td>${reg.reference}</td>
                                    <td>${formatDate(reg.date)}</td>
                                    <td>${reg.paymentMethod}</td>
                                    <td class="amount">${formatCurrency(reg.originalAmount)}</td>
                                    <td class="amount">${formatCurrency(reg.remainingAmount)}</td>
                                </tr>
                            `).join('')}
                    </tbody>
                </table>
            </div>
        ` : ''}

        ${statement.surplus.length > 0 ? `
            <div class="section">
                <div class="section-title">Surplus Disponibles</div>
                <table>
                    <thead>
                        <tr>
                            <th>Référence</th>
                            <th>Date</th>
                            <th class="amount">Montant</th>
                            <th>Règlement Ref</th>
                            <th>Remarques</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${statement.surplus.map(sur => `
                            <tr>
                                <td>${sur.reference}</td>
                                <td>${formatDate(sur.date)}</td>
                                <td class="amount">${formatCurrency(sur.amount)}</td>
                                <td>${sur.reglementRef}</td>
                                <td>${sur.remarks || ''}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        ` : ''}

        ${statement.unpaidDocuments.factures.length > 0 ? `
            <div class="section">
                <div class="section-title">Factures Non Payées</div>
                <table>
                    <thead>
                        <tr>
                            <th>Référence</th>
                            <th>Date</th>
                            <th class="amount">Montant Total</th>
                            <th class="amount">Montant Payé</th>
                            <th class="amount">Reste à Payer</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${statement.unpaidDocuments.factures.map(fac => `
                            <tr>
                                <td>${fac.reference}</td>
                                <td>${formatDate(fac.date)}</td>
                                <td class="amount">${formatCurrency(fac.totalAmount)}</td>
                                <td class="amount">${formatCurrency(fac.paidAmount)}</td>
                                <td class="amount">${formatCurrency(fac.remainingAmount)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        ` : ''}

        ${statement.unpaidDocuments.avoirs.length > 0 ? `
            <div class="section">
                <div class="section-title">Avoirs Non Utilisés</div>
                <table>
                    <thead>
                        <tr>
                            <th>Référence</th>
                            <th>Date</th>
                            <th class="amount">Montant</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${statement.unpaidDocuments.avoirs.map(av => `
                            <tr>
                                <td>${av.reference}</td>
                                <td>${formatDate(av.date)}</td>
                                <td class="amount">${formatCurrency(av.amount)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        ` : ''}

        <div class="no-print">
            <p style="text-align: center; margin-top: 20px; font-style: italic;">
                Ce document a été généré automatiquement.
            </p>
        </div>
    </body>
    </html>
  `;

  // Create a new window and print
  const printWindow = window.open('', '_blank');
  printWindow.document.write(printContent);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
};

// Print button component
const PrintStatementButton = ({ statement }) => {
  return (
    <PermissionButton permissionModule="clients" permissionAction="print"
      type="primary"
      icon={<PrinterOutlined />}
      onClick={() => printClientStatement(statement)}
    >
      Imprimer Relevé
    </PermissionButton>
  );
};

export default PrintStatementButton;
